import { audio } from '../systems/audio/AudioSystem.js';
import { dailySeed, normalizeSeed, seedLabel, SEED_MAX_LEN } from '../data/seeds.js';

/**
 * Seeded run pick screen (BULLET-HELL.md §D8).
 *
 * Two ways in, and they are the same mechanism wearing two hats:
 *
 * - **Today's daily.** One seed for everyone, rolling over at UTC midnight
 *   (data/seeds.js). This is the half the leaderboard needs: the board has
 *   always compared runs that faced different waves, different cards and
 *   different offers, and no amount of filtering fixes that — the runs were not
 *   comparable in the first place. A daily gives a day's rows one shared shape.
 *
 * - **A seed you type.** The same run, on demand, for the two things a fixed
 *   deck is good for outside a ladder: handing a friend the exact run you just
 *   played, and re-running one build decision without re-rolling the whole run.
 *
 * It plays **Act I**, deliberately, and does not route through act select. A
 * seed is a comparison frame, and Act I is the board every player has rows on
 * and the only act Endless continues into (data/acts.js) — a daily spread across
 * three ladders would divide the very population it exists to line up.
 */
export class SeedRunMenu {
  constructor(root, { onPick, onBack }) {
    this.root = root;
    this.onPick = onPick;
    this.onBack = onBack;
    this.error = '';
    this.el = document.createElement('div');
    this.el.className = 'menu-overlay seed-run-overlay';
    this._render();
    this.root.appendChild(this.el);
  }

  _render() {
    this.el.innerHTML = '';
    const today = dailySeed();

    const header = document.createElement('div');
    header.innerHTML = `
      <div class="menu-kicker">SEEDED RUN</div>
      <div class="menu-title">SAME DECK</div>
      <div class="menu-subtitle">A seed fixes what the run is made of — the waves, the cards each boss plays, the level-ups and Spoils it offers. Act I, fully rewarded, on the Act I board.</div>
    `;
    this.el.appendChild(header);

    const daily = document.createElement('button');
    daily.type = 'button';
    daily.className = 'menu-button menu-button-primary play-mode-card';
    daily.dataset.action = 'daily';
    daily.innerHTML = `<span class="play-mode-icon">☀</span><span><b>TODAY'S DAILY</b><small>${seedLabel(today)} — the same run as everyone else's today. Resets at midnight UTC.</small></span>`;
    daily.addEventListener('click', () => {
      audio.playSfx('click');
      this.onPick(today);
    });
    this.el.appendChild(daily);

    const form = document.createElement('form');
    form.className = 'settings-rows';

    const input = document.createElement('input');
    input.type = 'text';
    input.className = 'login-name-input';
    input.placeholder = 'Enter a seed';
    // The field is as long as the format allows (data/seeds.js), so a seed can
    // never be silently truncated into a different run than the one typed.
    input.maxLength = SEED_MAX_LEN;
    input.setAttribute('aria-label', 'Custom seed');
    form.appendChild(input);

    const submit = document.createElement('button');
    submit.type = 'submit';
    submit.className = 'menu-button';
    submit.textContent = 'Run This Seed';
    form.appendChild(submit);

    form.addEventListener('submit', (e) => {
      e.preventDefault();
      audio.playSfx('click');
      const seed = normalizeSeed(input.value);
      if (!seed) {
        // Reachable by an empty field or one holding only punctuation the
        // format drops — both mean "no seed", and starting an unseeded run from
        // the seeded-run screen would be the wrong kind of surprise.
        this.error = 'Enter a seed — letters, numbers and dashes.';
        this._render();
        return;
      }
      this.onPick(seed);
    });
    this.el.appendChild(form);

    // Normalisation is not hidden: the seed the run plays is the seed the board
    // files it under, so the player has to be able to see what theirs became.
    const note = document.createElement('div');
    note.className = 'menu-subtitle seed-run-note';
    note.textContent = 'Seeds are uppercased; anything else becomes a dash.';
    this.el.appendChild(note);

    if (this.error) {
      const error = document.createElement('div');
      error.className = 'login-error';
      error.textContent = this.error;
      this.el.appendChild(error);
    }

    const back = document.createElement('button');
    back.type = 'button';
    back.className = 'menu-button';
    back.textContent = 'Back';
    back.addEventListener('click', () => { audio.playSfx('click'); this.onBack(); });
    this.el.appendChild(back);
  }

  destroy() {
    this.el.remove();
  }
}
